export const ARCHIVED = 0;
export const LISTED = 1;
export const CHATTING = 2;
export const PURCHASED = 3;
export const HELD = 4;
export const SOLD = 5;

/**
 * The statuses a shopper may be shown while browsing: still buyable, nothing
 * pending or gone.
 *
 * PURCHASED (3) is a sale in progress, the same as HELD to a shopper: it reads
 * "Sale Pending" and cannot be bought (checkout accepts only LISTED and
 * CHATTING). It used to be browsable and labelled "Still Time", so browse
 * offered a Buy Now that checkout then refused, while the seller's Sale
 * Pending filter already counted it. No Next.js code writes 3; only the legacy
 * backend could. This is the single default for every browse, search and
 * showcase query — see `buildDefaultStatusFilter()` and `parseStatusFilter()`.
 *
 * It exists because three different policies had drifted apart. Elasticsearch's
 * default was `status > 0`, which admits SOLD: 31,811 sold listings were
 * eligible on the home showcases, /api/search/shop and /api/search/tags, and the
 * embed checkout widget reads the tags route — so a buyer could be offered an
 * item that had already sold. `parseStatusFilter` defaulted to [1,2,3,4], which
 * excluded SOLD but still leaked HELD. Tag showcases already used [1,2,3].
 *
 * HELD (sale pending) is deliberately out: the item is spoken for, and showing
 * it produces the same dead end as showing a sold one.
 *
 * A caller that genuinely needs sold listings must ask for them explicitly —
 * `?sold=1`, or an explicit `status`. The wishlist / recently-viewed / purchased
 * paths do exactly that via `buildGetByIdsQuery`, which stays on `status > 0` so
 * a watcher still sees their saved item labelled "Taken" rather than losing it.
 */
export const BROWSABLE_STATUSES = [LISTED, CHATTING];

/** Browsable plus the two end states, for callers that opt into sold items. */
export const BROWSABLE_WITH_SOLD_STATUSES = [LISTED, CHATTING, PURCHASED, HELD, SOLD];

export const STATUS_LABELS = [
  "Not Active",
  "Listed",
  "Popular",
  "Sale Pending",
  "Sale Pending",
  "Sold",
  "Sold",
  "Local"
];

/**
 * The same statuses, read from a WATCHER's side rather than the seller's.
 *
 * A seller looking at their own listing sees ARCHIVED as "Not Active" — accurate, and it
 * implies something they can undo. Someone with that listing on their wishlist has no such
 * control: the item is simply gone, and "Not Active" reads like a temporary outage rather
 * than a prompt to clear the row. Retired feed imports are the bulk of this — a dead
 * RescueGroups pet sat in 127 people's wishlists labelled "Not Active".
 *
 * This is a LABEL, not a status change. Moving those listings to SOLD in the database
 * would have done it too, and would have been a mistake: every bulk reindexer selects
 * `status >= 1` (scripts/reindex-all-via-api.ts, reindex-by-datasource.ts) and
 * reindex-sold-listings.ts selects status 5 exactly, while Elasticsearch's default filter
 * is `status > 0`. Marking them sold would have pushed long-dead listings back into browse
 * and search on the next reindex.
 */
export const WATCHER_STATUS_LABELS = STATUS_LABELS.map((label, status) =>
  status === ARCHIVED ? "Taken" : label
);
